import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ObjectStorageService} from './object-storage.service';
import {PrimusFormGroupService} from './primus-form-group.service';
import {SectionsContainer} from './definitions/sections-container';
import {ProgressDialogComponent} from '../shared/progress-dialog/progress-dialog.component';
import {ModelFactoryService} from './model-factory.service';
import {CmsApiService} from './cms-api.service';
import {CopyKeepService} from '../object-edit/copy-keep-checkbox/copy-keep.service';
import {ObjectViewAndData} from './definitions/object-view';
import {MetaField} from './definitions/meta-field';
import {Reference} from './definitions/reference';
import {SuperObjectModel} from './definitions/super-object-model';
import {BaseModel} from './definitions/base-model';
import {SearchParameters} from './definitions/search-parameters';
import {SolrFilterService} from './solr-filter.service';
import {GetArtifactAndOperationsParams} from './get-artifact-and-operations-params';
import {PrimusRouteService} from './primus-route.service';
import {FieldInputType} from "./definitions/field-input-type.enum";
import {SearchService} from "./search.service";
import {SearchReferenceService} from "./search-reference.service";
import {System} from "./definitions/system";
import {FormGroupParams} from "./definitions/form-group.params";
import {GenerateIdentifierParams} from "./definitions/generate-identifier-params";
import {
  isMissingMapIdDisplayValue,
  mappedArtifactDisplayName
} from './map-id-display-value';

export interface CreateSectionsContainerParams {
  useExistingObject: boolean;           // Set to true if supposed to create sections containing from existing object,
                                        // set to false if a new object shall be created
  objectType: string;                   // Must be set to a valid object type when creating new objects
  object: SuperObjectModel;             // Must be set to existing object data when creating from existing object,
                                        // unless object id is set
  objectId: string;                     // Must be set to an existing object id unless object is set
  getSourceObject: boolean;             // Set to true to fetch "source" object version from table instead of generating
                                        // complete object from DAO (used when viewing instead of editing object)
  templateGroupId: string;              // Relevant for object types supporting templates
  usePrimeFields: boolean;              // Set to true when prime fields should be displayed
  primeFieldNames: string[];            // With usePrimeFields, restrict to these leaf field names — the form group
                                        // is then built from the reduced set, so sectionsContainer.formGroup.valid
                                        // isn't dragged false by required prime fields the caller never renders
  isCopy: boolean;                      // Set to true if you need to retrieve a copy of the object from the server
  // If set, both object and operations for the object should be loaded at the same time
  operationTarget: string;
  createMultiple: boolean;              // Must be set in order to set the object property 'create_multiple' to true
}

interface SearchInfo {
  fieldName: string;
  searchParams: SearchParameters;
  displayField?: string;
}


@Injectable({
  providedIn: 'root'
})
export class ObjectEditService {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly copyKeepService: CopyKeepService = inject(CopyKeepService);
  private readonly formGroupService: PrimusFormGroupService = inject(PrimusFormGroupService);
  private readonly modelFactory: ModelFactoryService = inject(ModelFactoryService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly objectStorage: ObjectStorageService = inject(ObjectStorageService);
  private readonly primusRoute: PrimusRouteService = inject(PrimusRouteService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly searchService: SearchService = inject(SearchService);
  private readonly solrFilter: SolrFilterService = inject(SolrFilterService);

  currentObject: SuperObjectModel;

  async loadObjectGetSectionsContainer(objectId: string, getOriginalObject: boolean, templateGroupId?: string): Promise<SectionsContainer> {
    const objectViewAndData = await this.loadObjectViewAndData(objectId, getOriginalObject, templateGroupId);
    const sc = await this.formGroupService.getSectionsAndFormGroup(objectViewAndData.artifact, templateGroupId);
    this.currentObject = sc.rootObject;
    sc.objectView = objectViewAndData.view_data;
    return sc;
  }

  // eslint-disable-next-line max-len
  async loadObjectGetSectionsContainerAndOperations(objectId: string, getOriginalObject: boolean, operationTarget: string, templateGroupId?: string): Promise<SectionsContainer> {
    const getArtifactAndOperationsParams = new GetArtifactAndOperationsParams();
    getArtifactAndOperationsParams.operation_target = operationTarget;
    getArtifactAndOperationsParams.artifact_id = objectId;
    getArtifactAndOperationsParams.get_original_artifact = getOriginalObject;
    getArtifactAndOperationsParams.locale = this.primusRoute.params.lang;
    getArtifactAndOperationsParams.template_group_id = templateGroupId;
    getArtifactAndOperationsParams.timezone_offset = new Date().getTimezoneOffset();
    const objectViewAndData = await this.objectStorage.loadObjectWithViewDataAndOperations(getArtifactAndOperationsParams);
    const sc = await this.formGroupService.getSectionsAndFormGroup(objectViewAndData.artifact, templateGroupId);
    this.currentObject = sc.rootObject;
    sc.objectView = objectViewAndData.view_data;
    sc.operations = objectViewAndData.operations;
    return sc;
  }

  async loadObject(objectId: string): Promise<any> {
    return this.objectStorage.loadObject(objectId);
  }

  async loadObjects(objectIds: Array<string>): Promise<Array<any>> {
    return this.objectStorage.loadObjects(objectIds);
  }

  async setObjectValuesStoreObject(sectionsContainer: SectionsContainer, reloadObject?: boolean): Promise<SuperObjectModel> {
    return new Promise<any>((resolve, reject) => {
      if (sectionsContainer.formGroup.valid) {
        this.formGroupService.setObjectValuesFromForm(sectionsContainer.rootObject, sectionsContainer.formGroup);
        this.copyKeepService.removeNotKeep(sectionsContainer.rootObject);
        this.storeObjectShowProgressModal(sectionsContainer.rootObject, reloadObject).then(
          res => {
            if (reloadObject && !Array.isArray(res)) {
              sectionsContainer.rootObject = res;
            }
            resolve(res);
          },
          error_ => {
            reject(error_);
          }
        );
      }
    });
  }

  async storeObjectShowProgressModal(object: BaseModel, reloadObject?: boolean): Promise<SuperObjectModel | SuperObjectModel[]> {
    const progressModal = this.modalService.open(ProgressDialogComponent, {
      disableClose: true,
      panelClass: 'progress-modal'
    });

    const targetObjects = object?.create_multiple && Number.isInteger(object.create_multiple_count) && object.create_multiple_count > 0
      ? Array.from({length: object.create_multiple_count}, () => ({...object}))
      : [object];

    const results = [];

    try {
      for (const obj of targetObjects) {
        // Check for identification number availability when auto-generating identifiers
        // @ts-ignore
        if (obj?.identification_number?.identification_number === '_IGNORE_') {
          const nextIdentifier = await this.cms.getNextIdentifier({
            superobject_type_id: obj.superobject_type_id,
            // @ts-ignore
            collection_id: obj?.collection?.collection_id,
            // @ts-ignore
            context_id: obj?.identification_number?.context_id,
          } as GenerateIdentifierParams)

          if (!nextIdentifier.identifier) {
            console.warn('Exhausted identifier range for object type', obj.object_type);
            break;
          }
        }

        const storeRes = await this.objectStorage.storeObject(obj);
        if (reloadObject) {
          const objId = storeRes.artifact_id;
          if (objId) {
            const loadedObj = await this.objectStorage.loadObject(storeRes.artifact_id);
            results.push(loadedObj);
          } else {
            console.error('Re-loaded object had no artifact id!');
            results.push(storeRes);
          }
        } else {
          results.push(storeRes);
        }
      }
    } catch (e) {
      console.error('Store failed with message: ' + e?.message);
      throw e;
    } finally {
      progressModal.close();
    }

    return results.length > 1 ? results : results[0];
  }

  deleteObjectShowDialog(object: SuperObjectModel): Promise<void> {
    return new Promise(((resolve, reject) => {
      const progressModal = this.modalService.open(ProgressDialogComponent, {disableClose: true, panelClass: 'progress-modal'});
      this.objectStorage.deleteObject(object).then(
        () => {
          progressModal.close();
          resolve();
        },
        error_ => {
          progressModal.close(error_.error.message);
          reject(error_);
        }
      );
    }));
  }

  setNameField(parentRef: Reference, optionData: any, text: string) {
    let fieldNamePath = parentRef.add_new_params ? parentRef.add_new_params.name_field_path : null;
    if (!fieldNamePath && parentRef.object_type.startsWith('ct_')) {
      // Hardcoding field name path to 'name.name' for concepts
      fieldNamePath = 'name.name';
    }
    if (fieldNamePath) {
      const fieldNameSplit = fieldNamePath.split('.');
      let obj = optionData;
      fieldNameSplit.forEach((fieldPart, index) => {
        if (index < fieldNameSplit.length - 1) {
          obj[fieldPart] = {};
          obj = obj[fieldPart];
        } else {
          obj[fieldPart] = text;
        }
      });
    } else {
      console.warn('The reference parameter "add_new_params.name_field_path" must be set!!');
    }
  }

  async createSectionsContainer(params: CreateSectionsContainerParams): Promise<SectionsContainer> {
    let sectionsContainer: SectionsContainer;
    if (!params.useExistingObject) {
      if (params.usePrimeFields) {
        sectionsContainer = await this.createModelItemGetSectionsContainerForPrimeFields(
          params.objectType, params.object, false, params.primeFieldNames);
      } else {
        sectionsContainer = await this.createModelItemGetSectionsContainer(
          params.objectType, params.object, params.templateGroupId);
      }
    } else if (params.object) {
      if (params.usePrimeFields) {
        sectionsContainer = await this.getSectionsContainerForObjectPrimeFields(
          params.object, undefined, false, params.primeFieldNames);
      } else {
        sectionsContainer = await this.setModelItemGetSectionsContainer(
          params.object, params.templateGroupId, params.isCopy);
      }
    } else if (params.objectId) {
      if (params.operationTarget) {
        sectionsContainer = await this.loadObjectGetSectionsContainerAndOperations(
          params.objectId, !params.getSourceObject, params.operationTarget, params.templateGroupId);
      } else {
        sectionsContainer = await this.loadObjectGetSectionsContainer(
          params.objectId, !params.getSourceObject, params.templateGroupId);
      }
    } else {
      console.warn('Parameter "object" or "objectId", not set');
      return null;
    }
    sectionsContainer.debug = this.primusRoute.params.debug;
    if (params.createMultiple && sectionsContainer.rootObject.identification_number) {
      sectionsContainer.rootObject.create_multiple = params.createMultiple;
      sectionsContainer.rootObject.identification_number.identification_number = '_IGNORE_';
      sectionsContainer.formGroup.controls[
          `identification_number${System.keySep}identification_number`].setValue('_IGNORE_');
    }
    return sectionsContainer;
  }

  async createModelItemGetSectionsContainer(
    modelName: string, data?: SuperObjectModel, templateGroupId?: string): Promise<SectionsContainer> {
    const item = await this.createModelItem(modelName, data);
    return this.formGroupService.getSectionsAndFormGroup(<SuperObjectModel>item, templateGroupId);
  }

  async createModelItemGetSectionsContainerForPrimeFields(modelName: string,
                                                          data?: SuperObjectModel,
                                                          noSelectorFields?: boolean,
                                                          primeFieldNames?: string[]): Promise<SectionsContainer> {
    const modelItem = await this.createModelItem(modelName, data);
    const item = <SuperObjectModel>modelItem;
    return this.getSectionsContainerForObjectPrimeFields(item, modelName, noSelectorFields, primeFieldNames);
  }

  async createModelItem(modelName: string, data?: any): Promise<BaseModel> {
    const item = await this.modelFactory.createModelItemAsync(modelName, data);
    return this.setMapValues(item);
  }

  async setModelItemGetSectionsContainer(object: BaseModel, templateGroupId?: string, isCopy?: boolean): Promise<SectionsContainer> {
    const modeItem = <SuperObjectModel>await this.modelFactory.setModelItemAsync(object.object_type, object);
    return this.formGroupService.getSectionsAndFormGroup(modeItem, templateGroupId, isCopy);
  }

  async getSectionsContainerForObjectPrimeFields(object: SuperObjectModel, modelName?: string, noSelectorFields?: boolean,
                                                 primeFieldNames?: string[]): Promise<SectionsContainer> {
    let fields = await this.getPrimeFields(modelName || object.object_type);
    if (noSelectorFields) {
      fields = fields.filter(field => field.input_type !== FieldInputType.SEARCH_SELECTOR_MULTIPLE);
    }
    if (primeFieldNames) {
      // Filter before getFormGroup so the form group has controls only for
      // the fields the caller keeps — otherwise required prime fields that
      // are never rendered make formGroup.valid silently false.
      fields = fields.filter(field => primeFieldNames.includes(field.name));
    }
    const formGroup = this.formGroupService.getFormGroup(object, fields, true);
    const res = new SectionsContainer();
    res.rootObject = object;
    res.primeFields = fields;
    res.formGroup = formGroup;
    return res;
  }

  /*
  setSectionFields(object, editFieldsOnly: boolean, requiredOnly: boolean) {
    const sections = object.$$meta.sections;
    if (sections) {
      sections.forEach(section => {
        section.fields = [];
        for (const fieldName in object.$$meta) {
          if (object.$$meta.hasOwnProperty(fieldName)) {
            const fieldMeta = object.$$meta[fieldName];
            if (fieldMeta) {
              const sectionName = fieldMeta.section;
              if (sectionName === section.name) {
                if ((!editFieldsOnly || fieldMeta.edit) && (!requiredOnly || fieldMeta.required)) {
                  section.fields.push(fieldName);
                }
              }
            }
          }
        }
      });
    }
  }*/

  resetSectionsContainerFormGroup(sectionsContainer: SectionsContainer) {
    this.formGroupService.setSectionsContainerFormGroup(sectionsContainer);
  }

  // Useful for objects that have "root model" outside the regular
  // field container regime, in order to add items to the root model
  setTopModel(model: any, arrayName: string) {
    model[arrayName].forEach((item: any) => {
      item.$$topModel = model;
      item.$$topModelsArrayName = arrayName;
    });
  }

  async addInlineItem(modelName: string, field: MetaField, sectionsContainer: SectionsContainer, parentIndex: number): Promise<BaseModel> {
    const item = await this.createModelItem(modelName);
    sectionsContainer.rootObject[field.name] = item;
    this.formGroupService.setFormGroupField({
      rootObject: <BaseModel>sectionsContainer.rootObject,
      object: <BaseModel>sectionsContainer.rootObject,
      field: field,
      group: sectionsContainer.formGroup,
      parentKey: field.key,
      parentIndex: parentIndex
    } as FormGroupParams);
    return item;
  }

  private async setMapValues(modelObject: BaseModel): Promise<BaseModel> {
    const searches: SearchInfo[] = [];
    for (const [fieldName, metaField] of Object.entries(modelObject.$$meta)) {
      const fieldVal = modelObject[fieldName];
      if (fieldVal && metaField.field_type === 'map_id' && metaField.display === 'yes') {
        if (isMissingMapIdDisplayValue(modelObject[fieldName + '_value'])) {
          const searchInfo = {fieldName: fieldName, searchParams: {}} as SearchInfo;
          const ref = this.searchReferenceService.getSearchReferenceFromField(metaField);
          searchInfo.displayField = ref?.display_field;
          this.solrFilter.addFq(searchInfo.searchParams, 'object_type', ref.object_type);
          this.solrFilter.addFq(searchInfo.searchParams, 'artifact_id', fieldVal);
          searches.push(searchInfo);
        }
      }
    }
    if (searches.length) {
      for (let search of searches) {
        const searchRes = await this.searchService.search(search.searchParams);
        const artifacts = searchRes.artifacts;
        if (artifacts?.length) {
          modelObject[search.fieldName + '_value'] = mappedArtifactDisplayName(
            artifacts[0] as unknown as Record<string, unknown>, search.displayField);
        } else {
          console.warn('Could not find value searching for ' + JSON.stringify(search.searchParams));
        }
      }
    }
    return modelObject;
  }

  private async getPrimeFields(modelName: string): Promise<Array<any>> {
    return this.cms.getModelOverviewFields({
      modelName: modelName,
      type: 'prime'
    });
  }

  private async loadObjectViewAndData(objectId: string, getOriginalObject: boolean, templateGroupId: string): Promise<ObjectViewAndData> {
    return this.objectStorage.loadObjectWithViewData(objectId, getOriginalObject, templateGroupId);
  }

}
